import { removeArrayItem, updateArrayItem } from '@/util/array';
import zonesService from '@/api/zones-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { mapErrorsToInputs } from '@/util/forms';
import permitScannerLocationService from '@/api/permit-scanner-location-service';

const getDefaultFormItem = () => ({
  color: '',
  is_visible: true,
  is_available_for_vehicle_permits: false,
});

export const initialZonesFilterParams = () => ({});

export const getTranslatedZoneTitle = (title) => {
  const supportedZones = [
    'LSA – Labai svarbių asmenų terminalas',
    'S – Sterili zona',
    'L – Orlaivis ir jo stovėjimo aikštelės',
    'B – Bagažo kaupimo zona',
    'K – Kontroliuojama zona',
    'A – Aerodromo zona',
  ];

  if (supportedZones.includes(title)) {
    return i18n.t(`zone_titles.${title}`);
  }

  return title;
};

const state = {
  zones: null,
  zonesPagination: {},
  newZone: getDefaultFormItem(),
  editedZone: {},
  zoneValidationErrors: {},
  zonesFilterParams: initialZonesFilterParams(),
  zonePosts: null,
};

const getters = {
  zones(state) {
    if (!state.zones) {
      return null;
    }

    const translatedZones = [];

    for (let i = 0; i < state.zones.length; i++) {
      const z = state.zones[i];
      translatedZones.push({
        ...z,
        title: getTranslatedZoneTitle(z.title),
      });
    }
    return translatedZones;
  },

  zonesMap(state) {
    const map = {};

    if (!state.zones) {
      return {};
    }

    for (let i = 0; i < state.zones?.length; i++) {
      map[state.zones[i].id] = state.zones[i];
    }
    return map;
  },

  zonePostsMap(state) {
    const zones = {
      VNO: [],
      KUN: [],
      PLQ: [],
    };

    if (!state.zonePosts) {
      return zones;
    }

    for (let i = 0; i < state.zonePosts.length; i++) {
      if (state.zonePosts[i].includes('(VNO)')) {
        zones.VNO.push(state.zonePosts[i]);
      }
      if (state.zonePosts[i].includes('(KUN)')) {
        zones.KUN.push(state.zonePosts[i]);
      }
      if (state.zonePosts[i].includes('(PLQ)')) {
        zones.PLQ.push(state.zonePosts[i]);
      }
    }
    return zones;
  },
};

const mutations = {
  SET_ZONES(state, { data, current_page, per_page, total }) {
    state.zones = data;
    state.zonesPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.zonesFilterParams = params;
  },

  STORE_ZONE(state, zone) {
    state.zones = [...state.zones, zone];
    state.newZone = getDefaultFormItem();
    state.zoneValidationErrors = {};
    state.zonesPagination.total += 1;
  },

  SET_EDITED_ZONE(state, zone) {
    state.zoneValidationErrors = {};
    state.editedZone = JSON.parse(JSON.stringify(zone));
  },

  UPDATE_ZONE(state, zone) {
    state.zones = updateArrayItem(state.zones, zone);
  },

  DELETE_ZONE(state, zone) {
    state.zones = removeArrayItem(state.zones, zone);
    state.zonesPagination.total -= 1;
  },

  SET_ZONE_VALIDATION_ERRORS(state, validationErrors) {
    state.zoneValidationErrors = validationErrors;
  },

  RESET_ZONE_VALIDATION_ERRORS(state) {
    state.zoneValidationErrors = {};
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.zoneValidationErrors[field];
  },

  SET_ZONE_POSTS(state, posts) {
    state.zonePosts = posts;
  },
};

const actions = {
  fetchZones({ state, commit }, params) {
    commit('SET_FILTER_PARAMS', params);
    return zonesService.getPage(params).then((res) => {
      commit('SET_ZONES', res.data);
      if (params.page !== state.zonesFilterParams.page) {
        window.scrollTo(0, 0);
      }
    });
  },

  fetchZonePosts({ commit }) {
    return permitScannerLocationService.getAll().then((res) => {
      commit('SET_ZONE_POSTS', res.data);
    });
  },

  storeZone({ commit }, zone) {
    return zonesService
      .create(zone)
      .then((res) => {
        commit('STORE_ZONE', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('zone_created'));
        return res;
      })
      .catch((err) => {
        commit('SET_ZONE_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editZone({ state, commit }, zoneId) {
    const zone = state.zones?.find((z) => z.id === zoneId);
    if (zone) {
      commit('SET_EDITED_ZONE', zone);
      return Promise.resolve(zone);
    }
    return zonesService.getById(zoneId).then((res) => {
      commit('SET_EDITED_ZONE', res.data);
      return res.data;
    });
  },

  updateZone({ commit }, zone) {
    return zonesService
      .update(zone)
      .then((res) => {
        commit('UPDATE_ZONE', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('zone_updated'));
      })
      .catch((err) => {
        commit('SET_ZONE_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  deleteZone({ commit }, zone) {
    openConfirmDialog({
      title: i18n.t('confirm_zone_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      zonesService.delete(zone).then(() => {
        commit('DELETE_ZONE', zone);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('zone_deleted'));
      });
    });
  },

  clearZoneValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
